/**
 * @file upload-with-progress.ts
 * @description Загрузка файла multipart с процентом и отменой (phase19.16,
 * юридические документы SB139: «Загружаем cert.pdf — 64% [Отменить]»).
 *
 * `fetch` не сообщает прогресс отправки тела, поэтому — `XMLHttpRequest`
 * с `upload.onprogress`. Контракт ответа тот же, что у `apiFetch`:
 * `{ data }` → вернуть `data`; `{ error: { code, message, details } }` →
 * бросить `ApiError`. Кабинет вкладки (SC21) — заголовок `tabContextHeaders`.
 *
 * Особые случаи:
 *   - отмена (`signal.abort()`) → `ApiError` с кодом `upload_aborted` —
 *     вызывающий молча сбрасывает прогресс, без toast;
 *   - нет сети → `network_error`;
 *   - ответ не JSON (прокси отдал HTML на слишком большой файл) → код по
 *     статусу из `fallbackCodes` (413 → `legal_file_too_large` у документов).
 */

import { API_URL, ApiError, toApiError, type ApiErrorPayload } from "./api-client";
import { tabContextHeaders } from "./tab-context";

/** Код отмены загрузки пользователем. */
export const UPLOAD_ABORTED_CODE = "upload_aborted";

export interface UploadWithProgressOptions {
  /** Процент отправки 0…100. */
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  /** Код ошибки по HTTP-статусу, когда тело ответа не JSON. */
  fallbackCodes?: Readonly<Record<number, string>>;
}

/** Разобрать тело ответа; не JSON — null. */
function parseBody(text: string): { data?: unknown; error?: ApiErrorPayload } | null {
  if (!text) return {};
  try {
    return JSON.parse(text) as { data?: unknown; error?: ApiErrorPayload };
  } catch {
    return null;
  }
}

/** POST multipart с прогрессом; результат — `data` ответа. */
export function uploadWithProgress<T>(path: string, form: FormData, options: UploadWithProgressOptions = {}): Promise<T> {
  const { onProgress, signal, fallbackCodes = {} } = options;
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError({ code: UPLOAD_ABORTED_CODE, message: "Загрузка отменена" }, 0));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    for (const [name, value] of Object.entries(tabContextHeaders(path))) xhr.setRequestHeader(name, value);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };
    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort, { once: true });
    const done = () => signal?.removeEventListener("abort", onAbort);

    xhr.onload = () => {
      done();
      const body = parseBody(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((body?.data ?? null) as T);
        return;
      }
      const payload: ApiErrorPayload = body?.error ?? {
        code: fallbackCodes[xhr.status] ?? "api_error",
        message: `HTTP ${xhr.status}`,
      };
      // Общий разбор api-client: 401, «нет активного кабинета», «чужой кабинет вкладки».
      reject(toApiError(path, xhr.status, payload));
    };
    xhr.onerror = () => {
      done();
      reject(new ApiError({ code: "network_error", message: "Сетевая ошибка" }, 0));
    };
    xhr.onabort = () => {
      done();
      reject(new ApiError({ code: UPLOAD_ABORTED_CODE, message: "Загрузка отменена" }, 0));
    };
    xhr.send(form);
  });
}
